import React, { useMemo } from "react";
import { Text, Stack, Group } from "@mantine/core";

import { DisplayActivation } from "@/utils/api";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  AppButton,
} from "@/components/generic";
import { formatDateTime, parseUserAgent } from "@/utils";

export interface DisplayActivationsTableProps {
  /** Array of display activations to display */
  activations: DisplayActivation[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** ID of display activation just edited or added */
  editedItemId?: string | null | undefined;

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when remove action is clicked */
  onRemoveActivation?: (activation: DisplayActivation) => void;

  onEditActivation?: (activation: DisplayActivation) => void;

  onReload?: (activation: DisplayActivation) => void;

  /** ID of activation currently being removed */
  removingActivationId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show empty state */
  showEmptyState?: boolean;

  hideOnNoRecords?: boolean;
}

export const DisplayActivationsTable: React.FC<
  DisplayActivationsTableProps
> = ({
  activations,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  editedItemId,
  onSortChange,
  onLoadMore,
  onRemoveActivation,
  onEditActivation,
  onReload,
  hideOnNoRecords,
  removingActivationId,
  size = "md",
  showEmptyState = true,
}) => {
  // Define table columns
  const columns: AppTableColumn<DisplayActivation>[] = useMemo(
    () => [
      // {
      //   key: "status",
      //   label: "Status",
      //   sortable: true,
      //   width: 150,
      //   minWidth: 150,
      //   render: (_, record) => {
      //     const { status, color } = getScreenStatus(record.pingedAt);
      //     return (
      //       <Badge color={color} variant="light" size="sm">
      //         {status}
      //       </Badge>
      //     );
      //   },
      // },
      {
        key: "name",
        label: "Screen Info",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (_, record) => (
          <Stack gap={2}>
            <Text fw={500} size="md">
              {record.name || "No Name Given"}
            </Text>
            <Text size="sm">{record.display?.name || "N/A"}</Text>
            <Text size="xs" c="dimmed">
              version {record.appVersion || "N/A"}
            </Text>
          </Stack>
        ),
      },
      {
        key: "systemInfo",
        label: "Device Info",
        width: 200,
        minWidth: 200,
        render: (_, record) => {
          const { browser, os, device } = parseUserAgent(record.userAgent);
          return (
            <Stack gap={2}>
              <Text size="sm">{browser}</Text>
              <Text size="xs" c="dimmed">
                {device}, {os}
              </Text>
              <Text size="xs" c="dimmed">
                Resolution {record.resolution || "N/A"}
              </Text>
              <Text size="xs" c="dimmed">
                IP Address {record.ipAddress || "N/A"}
              </Text>
            </Stack>
          );
        },
      },
      {
        key: "content",
        label: "Content",
        width: 200,
        minWidth: 200,
        render: (_, record) => (
          <Stack gap={2}>
            <Text size="sm">{record.display?.theme?.name || "N/A"}</Text>
            <Text size="xs" c="dimmed">
              {record.display?.emergencyContentBlock?.name ||
                "No emergency content"}
            </Text>
          </Stack>
        ),
      },
      {
        key: "pingedAt",
        label: "Last Ping",
        sortable: true,
        width: 140,
        render: (_, record) => (
          <Text size="sm" c="dimmed">
            {formatDateTime(record.pingedAt, "relative")}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        width: 180,
        minWidth: 180,
        align: "left",
        render: (_, record) => {
          return (
            <Group gap={"xs"}>
              <AppButton
                iconName="Reload"
                variant="secondary"
                size="xs"
                onClick={() => onReload?.(record)}
                tooltip="Reload the screen"
              />
              <RecordActions
                context={record}
                onRemoveClick={onRemoveActivation}
                onEditClick={onEditActivation}
                removeInProgress={removingActivationId === record.id}
              />
            </Group>
          );
        },
      },
    ],
    [onRemoveActivation, removingActivationId]
  );

  if (hideOnNoRecords === true && (!activations || activations.length == 0)) {
    return null;
  }

  return (
    <AppTable
      data={activations}
      hideOnNoData={!showEmptyState}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(activation) => activation?.id || ""}
      highlightRow={(activation) => activation?.id == editedItemId}
      withBorder
      highlightOnHover
      striped
      size={size}
      emptyState={{
        title: "No Connected Devices",
        description: "To connect a device you need first create a display ",
        iconName: "MoodPuzzled",
        onClick: null,
      }}
    />
  );
};
